import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroll-component';
import { NavTabs } from '@components/NavTabs';
import { Typography } from '@components/Typography';
import { useTranslation, useQueryParams, useConversations } from '@hooks';
import { AssignedStatus, ConversationSorting } from '@constants/conversations';
import { AssignmentType } from '@constants/inbox';
import { getFullName } from '@helpers/userName';
import { contactsActions, conversationsActions, inboxActions } from '@actions';
import { ReturnIcon } from 'assets/images/icons';
import Dialog from './Dialog';
import ConversationsItemSkeleton from './ConversationsItemSkeleton';
import SelectedDialogsToolbar from './SelectedDialogsToolbar';
import FiltersPanel from '../FiltersPanel';
import { ConversationsWrapper, TabsWrapper, ConversationsToolbar } from './styles';

const Dialogs = ({ isMobile, filter, setFilter, activeConversation, setActiveConversation, activeConversationRef }) => {
    const [selectedConversations, setSelectedConversations] = useState(new Map());

    const language = useSelector(state => state.auth.user?.language);
    const { data, loading, counters, pageToken } = useSelector(state => state.conversations);
    const currentUser = useSelector(state => state.auth.user);
    const users = useSelector(state => state.account?.account?.users || []);
    const agents = useSelector(state => state.agents?.data || []);

    const history = useHistory();
    const params = useQueryParams();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { readConversationById } = useConversations();

    const queryConversationId = params.get('conversationId');

    const availableTabs = [
        { label: t('InboxPage.mine'), value: AssignedStatus.Mine, counter: counters?.assigned || 0 },
        {
            label: t('InboxPage.unassigned'),
            value: AssignedStatus.Unassigned,
            counter: counters?.unassigned || 0,
        },
        { label: t('common.all'), value: AssignedStatus.All, counter: counters?.all || 0 },
    ];

    const assigneesOptions = useMemo(() => {
        const usersOptions = users.map(user => ({
            value: user.id,
            label: getFullName(user.firstName, user.lastName),
            type: AssignmentType.User,
        }));
        const agentsOptions = agents.map(agent => ({
            value: agent.id,
            label: agent.name,
            type: AssignmentType.Agent,
        }));

        const groupedOptions = [];
        groupedOptions.push({ group: t('common.users'), children: usersOptions });

        if (agentsOptions.length > 0) {
            groupedOptions.push({ group: t('Sidebar.agents'), children: agentsOptions });
        }

        groupedOptions.push({
            group: t('AnalyticsReviews.others'),
            children: [{ value: null, label: t('common.unassigned'), type: AssignmentType.Unassigned }],
        });

        return groupedOptions;
    }, [users, agents, t]);

    useEffect(() => {
        if (data.length > 0) {
            /* INFO: when data updated and no selected conversation, select the first one
                if mobile, not need to select the first conversation because it will show always messages screen
            */
            if (!activeConversationRef.current && !isMobile) {
                onSetActiveConversation(data[0]);
                return;
            }
        }
    }, [data]);

    const onSetActiveConversation = conversation => {
        setActiveConversation(conversation);
        if (!conversation?.read) {
            readConversationById(conversation?.id);
        }
        dispatch(inboxActions.getHistory(conversation?.id));
        if (conversation?.contactId !== activeConversationRef.current?.contactId) {
            dispatch(contactsActions.getContact(conversation?.contactId));
        }
    };

    const onSelectConversation = id => {
        setSelectedConversations(prev => {
            const newMap = new Map(prev);
            if (newMap.has(id)) {
                newMap.delete(id);
            } else {
                newMap.set(id, true);
            }
            return newMap;
        });
    };

    const loadMoreConversations = () => {
        if (!pageToken) return;
        dispatch(conversationsActions.loadMore(filter, pageToken));
    };

    const onApplyFilters = () => {
        if (queryConversationId) {
            history.replace();
        }
        const selectedAssignees = filter.assignees;

        const oneSelected = selectedAssignees.length === 1;

        let assignment;
        if (oneSelected) {
            if (selectedAssignees[0]?.value === currentUser?.id) {
                assignment = AssignedStatus.Mine;
            } else if (selectedAssignees[0]?.value === null) {
                assignment = AssignedStatus.Unassigned;
            } else if (assigneesOptions.length === selectedAssignees.length) {
                assignment = AssignedStatus.All;
            } else {
                assignment = AssignedStatus.Assigned;
            }
        } else {
            if (assigneesOptions.length === selectedAssignees.length) {
                assignment = AssignedStatus.All;
            } else {
                assignment = AssignedStatus.Assigned;
            }
        }
        const newFilterValues = { ...filter, assignment, conversationId: null };
        setFilter(newFilterValues);
        setSelectedConversations(new Map());
        dispatch(conversationsActions.getCounters(newFilterValues));
        dispatch(conversationsActions.get(newFilterValues));
    };

    const onAssignTabClick = tabIndex => {
        if (queryConversationId) {
            history.replace();
        }
        let assignees = [];
        if (tabIndex === AssignedStatus.Unassigned) {
            assignees = [{ value: null, type: AssignmentType.Unassigned }];
        } else if (tabIndex === AssignedStatus.Mine) {
            assignees = [{ value: currentUser?.id, type: AssignmentType.User }];
        } else if (tabIndex === AssignedStatus.All) {
            assignees = [
                ...users.map(user => ({ value: user.id, type: AssignmentType.User })),
                { value: null, type: AssignmentType.Unassigned },
                ...agents.map(a => ({ value: a.id, type: AssignmentType.Agent })),
            ];
        }
        const newFilterValues = {
            ...filter,
            assignment: tabIndex,
            conversationId: null,
            assignees,
        };
        setFilter(newFilterValues);
        setSelectedConversations(new Map());
        dispatch(conversationsActions.get(newFilterValues));
    };

    const onClearQueryParams = () => {
        history.replace();
        const newFilterValues = { ...filter, conversationId: null };
        setFilter(newFilterValues);
        setSelectedConversations(new Map());
        dispatch(conversationsActions.get(newFilterValues));
    };

    const TabIndexRemap = {
        [AssignedStatus.Mine]: 0,
        [AssignedStatus.Unassigned]: 1,
        [AssignedStatus.All]: 2,
        [AssignedStatus.Assigned]: false,
    };

    const sortedConversations = useMemo(() => {
        return [...data].sort((a, b) => {
            if (filter.sorting === ConversationSorting.ActivityOldestFirst) {
                return new Date(a.lastActivityAt).getTime() - new Date(b.lastActivityAt).getTime();
            }
            if (filter.sorting === ConversationSorting.CreatedAtNewestFirst) {
                return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
            }
            if (filter.sorting === ConversationSorting.CreatedAtOldestFirst) {
                return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
            }
            return new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime();
        });
    }, [data]);

    const allSelected = selectedConversations.has('all');

    return (
        <div>
            <TabsWrapper hasSelected={!!filter.conversationId || selectedConversations.size > 0}>
                <NavTabs
                    tabLinks={availableTabs}
                    onTabClick={onAssignTabClick}
                    currentTabIndex={TabIndexRemap[filter.assignment]}
                    withCounter
                    withLink={false}
                />
                <FiltersPanel
                    onApplyFilters={onApplyFilters}
                    filter={filter}
                    setFilter={setFilter}
                    assigneesOptions={assigneesOptions}
                />
            </TabsWrapper>
            {selectedConversations.size > 0 ? (
                <SelectedDialogsToolbar
                    selectedConversations={selectedConversations}
                    setSelectedConversations={setSelectedConversations}
                    counters={counters}
                    filter={filter}
                />
            ) : null}
            {filter.conversationId && selectedConversations.size === 0 ? (
                <ConversationsToolbar>
                    <Typography
                        variant="caption"
                        sx={{
                            cursor: 'pointer',
                            color: '#1F4C5C',
                            display: 'flex',
                            alignItems: 'center',
                            width: 'fit-content',
                        }}
                        onClick={onClearQueryParams}
                    >
                        <ReturnIcon style={{ marginRight: '8px' }} />
                        <span>{t('InboxPage.backToAllConversations')}</span>
                    </Typography>
                </ConversationsToolbar>
            ) : null}
            <ConversationsWrapper
                id="pl-feed-conversations"
                isEmpty={!loading && data.length === 0}
                hasConversationId={!!filter.conversationId}
            >
                {!loading && data.length === 0 && (
                    <Typography variant="body2">{t('InboxPage.noConversations')}</Typography>
                )}
                {loading && [...Array(5).keys()].map(item => <ConversationsItemSkeleton key={item} />)}
                {!loading && !!data.length && (
                    <InfiniteScroll
                        dataLength={data.length}
                        next={loadMoreConversations}
                        hasMore={!!pageToken}
                        loader={<ConversationsItemSkeleton />}
                        scrollableTarget="pl-feed-conversations"
                    >
                        {sortedConversations.map(conversation => {
                            const isActive = conversation.id === activeConversation?.id;
                            const isSelected =
                                selectedConversations.has(conversation.id) || selectedConversations.has('all');
                            return (
                                <Dialog
                                    key={conversation.id}
                                    data={conversation}
                                    onSetActive={isActive ? null : () => onSetActiveConversation(conversation)}
                                    isActive={isActive}
                                    isSelected={isSelected}
                                    allSelected={allSelected}
                                    onSelect={() => onSelectConversation(conversation.id)}
                                    users={users}
                                    language={language}
                                    isMobile={isMobile}
                                />
                            );
                        })}
                    </InfiniteScroll>
                )}
            </ConversationsWrapper>
        </div>
    );
};

export default Dialogs;
